import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Plus, Loader2, Trash2, Eye, EyeOff, Image as ImageIcon, X, Trophy, Megaphone, Users, Infinity as InfinityIcon, Clock, StopCircle, ChevronDown, ChevronUp, CalendarClock, Smartphone, Shirt, DoorClosed, Sparkles, Play, Tag } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { adminGet, adminPost, adminDelete, reportError, formatDate, BACKEND_URL } from './adminApi';
import { compressImage } from '../../lib/easerApi';

const MODES = [
  { id: 'popup', label: 'Pop-up au démarrage', hint: 'affichée à chaque ouverture de l\'application pendant la durée d\'affichage' },
  { id: 'banner', label: 'Bandeau en haut', hint: 'bandeau discret en haut de l\'espace Easer, refermable' },
  { id: 'both', label: 'Pop-up puis bandeau', hint: 'pop-up jusqu\'à la première lecture, puis bandeau pendant la durée d\'affichage' },
];
const MODE_LABELS = Object.fromEntries(MODES.map((m) => [m.id, m.label]));
const EMPTY = { title: '', body: '', kind: 'message', display_mode: 'popup', display_days: 7, keep_mode: 'permanent', keep_days: 30, targets: [], image: null, when: 'now', starts_at: '', send_push: true };

const monthName = () => { const m = new Date().toLocaleDateString('fr-FR', { month: 'long' }); return /^[aeiouy]/i.test(m) ? `d'${m}` : `de ${m}`; };
/** Ready-made notifications: pick one, adjust, send. */
export const TEMPLATES = [
  { id: 'challenge', label: 'Challenge du mois', icon: Trophy, values: { kind: 'challenge', display_mode: 'both', display_days: 30, keep_mode: 'permanent',
    title: `Challenge ${monthName()} : 10 avis clients 5 étoiles`, body: `Objectif du mois : décrocher 10 avis clients 5 étoiles dans l'Espace client.\nChaque avis compte, pensez à proposer au client de laisser son appréciation après la prestation.\nRésultats et récompense annoncés en fin de mois. Bonne chance à toutes !` } },
  { id: 'tenue', label: 'Rappel tenue', icon: Shirt, values: { kind: 'message', display_mode: 'popup', display_days: 7, keep_mode: 'days', keep_days: 30,
    title: 'Rappel : tenue Ptitservices obligatoire', body: `Petit rappel : la tenue Ptitservices (polo et gilet) est obligatoire chez chaque client.\nUne tenue propre et complète, c'est l'image de l'agence. Besoin d'un nouveau polo ? Demandez-le à l'agence.` } },
  { id: 'fermeture', label: 'Fermeture agence', icon: DoorClosed, values: { kind: 'message', display_mode: 'both', display_days: 7, keep_mode: 'days', keep_days: 15,
    title: 'Fermeture exceptionnelle de l\'agence', body: `L'agence sera fermée le [date] (toute la journée).\nVos interventions sont maintenues normalement. En cas d'urgence, contactez le 03 74 47 47 82.` } },
];

/** Admin: agency notifications (challenges / messages) pushed to the Easer portal, with read receipts. */
export function AdminEaserNotifications({ adminKey, employees = [], jobTags = [] }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [readsOf, setReadsOf] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/easer/notifications', adminKey);
      setItems(res.data.items);
    } catch (err) {
      reportError('notifications Easer', err, () => toast.error('Impossible de charger les notifications'));
      setItems((i) => i || []);
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const stop = async (n) => {
    setBusyId(n.id);
    try {
      await adminPost(`/api/admin/easer/notifications/${n.id}/stop`, adminKey, {}, {});
      toast.success('Affichage arrêté : la notification reste consultable dans GRH');
      await load();
    } catch (err) {
      reportError('arrêt notification', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setBusyId(null);
    }
  };

  const startNow = async (n) => {
    setBusyId(n.id);
    try {
      await adminPost(`/api/admin/easer/notifications/${n.id}/start`, adminKey, {}, {});
      toast.success('Notification diffusée dès maintenant aux Easers');
      await load();
    } catch (err) {
      reportError('diffusion immédiate', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setBusyId(null);
    }
  };

  const purge = async (n) => {
    if (!window.confirm(`Purger définitivement « ${n.title} » ? Elle disparaîtra de l'espace Easer de toutes les Easers.`)) return;
    setBusyId(n.id);
    try {
      await adminDelete(`/api/admin/easer/notifications/${n.id}`, adminKey);
      toast.success('Notification purgée');
      await load();
    } catch (err) {
      reportError('purge notification', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="p-5" data-testid="admin-easer-notifications">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><Bell className="h-4 w-4" /> Notifications aux Easers</h3>
          <p className="text-sm text-muted-foreground">Challenges et messages affichés au démarrage de l'espace Easer (GRH › Notifications), avec accusé de première lecture.</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="notif-create-open"><Plus className="h-4 w-4 mr-1" /> Nouvelle notification</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="notif-templates">
        <span className="text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Modèles rapides :</span>
        {TEMPLATES.map((t) => (
          <Button key={t.id} size="sm" variant="outline" onClick={() => setCreating(t.id)} data-testid={`notif-template-${t.id}`}><t.icon className="h-4 w-4 mr-1" /> {t.label}</Button>
        ))}
      </div>

      {items === null ? <Skeleton className="h-20" /> : items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="admin-notifs-empty">Aucune notification pour le moment.</p>
      ) : (
        <ul className="divide-y divide-border" data-testid="admin-notifs-list">
          {items.map((n) => (
            <li key={n.id} className="py-3 flex flex-wrap items-start gap-3" data-testid="admin-notif-item">
              <span className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${n.kind === 'challenge' ? 'bg-[hsl(var(--brand-apricot))]/15 text-[hsl(var(--brand-apricot))]' : 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]'}`}>
                {n.kind === 'challenge' ? <Trophy className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}
              </span>
              <div className="min-w-[220px] flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium" data-testid="admin-notif-title">{n.title}</p>
                  {n.scheduled ? <Badge className="bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]" data-testid="admin-notif-status"><CalendarClock className="h-3 w-3 mr-1" /> Programmée</Badge>
                    : n.display_active ? <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]" data-testid="admin-notif-status">Affichée</Badge>
                    : n.expired ? <Badge variant="outline" data-testid="admin-notif-status">Expirée</Badge>
                      : <Badge variant="secondary" data-testid="admin-notif-status">Conservée</Badge>}
                  {n.has_image && <ImageIcon className="h-4 w-4 text-muted-foreground" aria-label="Avec image" />}
                </div>
                {n.body && <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{n.body}</p>}
                <p className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>{MODE_LABELS[n.display_mode] || n.display_mode}</span>
                  {n.scheduled && <span className="font-medium text-[hsl(var(--primary))]" data-testid="admin-notif-starts"><CalendarClock className="inline h-3 w-3 align-text-bottom" /> débute le {formatDate(n.starts_at, true)} — pas encore visible des Easers</span>}
                  <span><Clock className="inline h-3 w-3 align-text-bottom" /> affichage jusqu'au {formatDate(n.display_until)}</span>
                  <span data-testid="admin-notif-push"><Smartphone className="inline h-3 w-3 align-text-bottom" /> {!n.send_push ? 'sans push' : n.scheduled ? 'push au début' : n.push_result?.skipped ? 'push non envoyé' : n.push_result ? `push : ${n.push_result.sent} téléphone${n.push_result.sent > 1 ? 's' : ''}` : 'push en cours'}</span>
                  <span>{n.permanent ? <><InfinityIcon className="inline h-3 w-3 align-text-bottom" /> conservée en permanence</> : `conservée jusqu'au ${formatDate(n.keep_until)}`}</span>
                  <span><Users className="inline h-3 w-3 align-text-bottom" /> {n.targets.length ? n.target_names.join(', ') : 'toutes les Easers'}</span>
                  <span>créée le {formatDate(n.created_at, true)}</span>
                </p>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setReadsOf(readsOf?.id === n.id ? null : n)} data-testid="admin-notif-reads">
                  <Eye className="h-4 w-4 mr-1" /> {n.read_count} lecture{n.read_count > 1 ? 's' : ''} {readsOf?.id === n.id ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                </Button>
                {n.scheduled && <Button size="sm" onClick={() => startNow(n)} disabled={busyId === n.id} data-testid="admin-notif-start-now"><Play className="h-4 w-4 mr-1" /> Diffuser maintenant</Button>}
                {n.display_active && <Button size="sm" variant="outline" onClick={() => stop(n)} disabled={busyId === n.id} data-testid="admin-notif-stop"><StopCircle className="h-4 w-4 mr-1" /> Arrêter l'affichage</Button>}
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => purge(n)} disabled={busyId === n.id} data-testid="admin-notif-purge">
                  {busyId === n.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </div>
              {readsOf?.id === n.id && (
                <div className="w-full rounded-lg bg-[hsl(var(--secondary))]/70 p-3 text-sm" data-testid="admin-notif-reads-panel">
                  {n.reads.length === 0 ? <p className="text-muted-foreground flex items-center gap-1"><EyeOff className="h-4 w-4" /> Pas encore lue.</p> : (
                    <ul className="grid sm:grid-cols-2 gap-1">
                      {n.reads.map((r) => <li key={r.user_id} className="flex justify-between gap-2"><span className="font-medium">{r.user_name || `#${r.user_id}`}</span><span className="text-muted-foreground">{formatDate(r.read_at, true)}</span></li>)}
                    </ul>
                  )}
                  {n.targets.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">Non lue par : {n.target_names.filter((_, i) => !n.reads.some((r) => r.user_id === n.targets[i])).join(', ') || 'personne'}</p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <CreateDialog open={!!creating} template={typeof creating === 'string' ? TEMPLATES.find((t) => t.id === creating) : null} adminKey={adminKey} employees={employees} jobTags={jobTags} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
    </Card>
  );
}

const localDateTimeDefault = () => { const d = new Date(Date.now() + 24 * 3600 * 1000); d.setHours(8, 0, 0, 0); const p = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

function CreateDialog({ open, template, adminKey, employees, jobTags = [], onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setForm({ ...EMPTY, starts_at: localDateTimeDefault(), ...(template?.values || {}) }); }, [open, template]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      set('image', await compressImage(file, 1600, 0.85));
    } catch {
      toast.error('Image illisible');
    }
  };

  const toggleTarget = (id) => set('targets', form.targets.includes(id) ? form.targets.filter((t) => t !== id) : [...form.targets, id]);
  // Job tag (Dolibarr user category): select / unselect every Easer of that trade at once.
  const tagMembers = (tag) => (tag.user_ids || []).map(Number).filter((id) => employees.some((e) => Number(e.id) === id));
  const tagFullySelected = (tag) => { const m = tagMembers(tag); return m.length > 0 && m.every((id) => form.targets.includes(id)); };
  const toggleTag = (tag) => {
    const m = tagMembers(tag);
    set('targets', tagFullySelected(tag) ? form.targets.filter((id) => !m.includes(id)) : [...new Set([...form.targets, ...m])]);
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Le titre est requis');
    setSaving(true);
    try {
      await adminPost('/api/admin/easer/notifications', adminKey, {}, {
        title: form.title.trim(), body: form.body.trim(), kind: form.kind, display_mode: form.display_mode,
        display_days: Number(form.display_days) || 7, keep_days: form.keep_mode === 'permanent' ? null : (Number(form.keep_days) || 30),
        targets: form.targets, image: form.image, send_push: form.send_push,
        starts_at: form.when === 'later' && form.starts_at ? new Date(form.starts_at).toISOString() : null,
      });
      toast.success(form.when === 'later' ? `Notification programmée pour le ${new Date(form.starts_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}` : 'Notification envoyée aux Easers');
      onCreated();
    } catch (err) {
      reportError('création notification', err, (m) => toast.error(m || 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="notif-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Space_Grotesk']">{template ? `Modèle : ${template.label}` : 'Nouvelle notification'}</DialogTitle>
          <DialogDescription>{template ? 'Texte pré-rempli, ajustez-le puis envoyez.' : 'Affichée dans l\'espace Easer au démarrage et conservée dans GRH › Notifications.'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => set('kind', v)}>
                <SelectTrigger data-testid="notif-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="message">Message</SelectItem>
                  <SelectItem value="challenge">Challenge</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Affichage</Label>
              <Select value={form.display_mode} onValueChange={(v) => set('display_mode', v)}>
                <SelectTrigger data-testid="notif-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">{MODES.find((m) => m.id === form.display_mode)?.hint}</p>
          <div className="space-y-1.5">
            <Label htmlFor="notif-title">Titre</Label>
            <Input id="notif-title" value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={120} placeholder="Challenge du mois : 10 avis 5 étoiles" data-testid="notif-title" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notif-body">Texte</Label>
            <Textarea id="notif-body" value={form.body} onChange={(e) => set('body', e.target.value)} rows={4} maxLength={4000} placeholder="Détails du challenge ou du message..." data-testid="notif-body" />
          </div>
          <div className="space-y-1.5">
            <Label>Image (facultative)</Label>
            {form.image ? (
              <div className="relative">
                <img src={form.image} alt="" className="w-full max-h-48 object-contain rounded-lg border border-border bg-secondary/40" />
                <button type="button" onClick={() => set('image', null)} aria-label="Retirer l'image" className="absolute top-2 right-2 h-8 w-8 rounded-full bg-background/90 border border-border flex items-center justify-center" data-testid="notif-image-remove"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="notif-image-pick"><ImageIcon className="h-4 w-4 mr-1" /> Ajouter une image</Button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="notif-image-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="notif-display-days">Durée d'affichage (jours)</Label>
              <Input id="notif-display-days" type="number" min={1} max={365} value={form.display_days} onChange={(e) => set('display_days', e.target.value)} data-testid="notif-display-days" />
            </div>
            <div className="space-y-1.5">
              <Label>Conservation dans GRH</Label>
              <Select value={form.keep_mode} onValueChange={(v) => set('keep_mode', v)}>
                <SelectTrigger data-testid="notif-keep-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="permanent">Permanente (jusqu'à purge)</SelectItem>
                  <SelectItem value="days">Pendant une durée</SelectItem>
                </SelectContent>
              </Select>
              {form.keep_mode === 'days' && <Input type="number" min={1} max={3650} value={form.keep_days} onChange={(e) => set('keep_days', e.target.value)} aria-label="Jours de conservation" data-testid="notif-keep-days" />}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Quand ?</Label>
            <div className="grid grid-cols-2 gap-2">
              {[['now', 'Maintenant'], ['later', 'Programmer (date + heure)']].map(([id, label]) => (
                <button key={id} type="button" onClick={() => set('when', id)} data-testid={`notif-when-${id}`}
                  className={`h-10 rounded-lg border text-sm font-medium transition-colors ${form.when === id ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border hover:bg-secondary'}`}>{label}</button>
              ))}
            </div>
            {form.when === 'later' && (
              <div className="flex items-center gap-2 pt-1">
                <CalendarClock className="h-4 w-4 text-muted-foreground" />
                <Input type="datetime-local" value={form.starts_at} onChange={(e) => set('starts_at', e.target.value)} aria-label="Date et heure de début" data-testid="notif-starts-at" />
              </div>
            )}
            <p className="text-xs text-muted-foreground">{form.when === 'later' ? 'Invisible pour les Easers avant cette date ; l\'affichage et la conservation démarrent à ce moment.' : 'Visible immédiatement dans l\'espace Easer.'}</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer rounded-lg border border-border p-3">
            <Checkbox checked={form.send_push} onCheckedChange={(v) => set('send_push', !!v)} data-testid="notif-send-push" />
            <span className="flex items-center gap-1.5"><Smartphone className="h-4 w-4 text-muted-foreground" /> Envoyer aussi une notification push sur les téléphones abonnés{form.when === 'later' ? ' au moment du début' : ''}</span>
          </label>
          <div className="space-y-1.5">
            <Label>Destinataires</Label>
            {jobTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="notif-target-tags">
                <span className="text-xs text-muted-foreground mr-1">Par métier :</span>
                {jobTags.map((t) => (
                  <Button key={t.id} type="button" size="sm" variant={tagFullySelected(t) ? 'default' : 'outline'} className="h-8 rounded-full" onClick={() => toggleTag(t)} disabled={tagMembers(t).length === 0} data-testid={`notif-target-tag-${t.id}`}>
                    <Tag className="h-3.5 w-3.5 mr-1" /> {t.short} <span className="ml-1 opacity-70">{tagMembers(t).length}</span>
                  </Button>
                ))}
              </div>
            )}
            <div className="rounded-lg border border-border p-3 space-y-2 max-h-48 overflow-y-auto" data-testid="notif-targets">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <Checkbox checked={form.targets.length === 0} onCheckedChange={() => set('targets', [])} data-testid="notif-target-all" /> Toutes les Easers
              </label>
              {employees.map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.targets.includes(Number(e.id))} onCheckedChange={() => toggleTarget(Number(e.id))} data-testid={`notif-target-${e.id}`} /> {e.name || e.login}
                  {(e.tags || []).map((tg) => <Badge key={tg} variant="outline" className="text-[10px] py-0 px-1.5 font-normal text-muted-foreground">{tg}</Badge>)}
                </label>
              ))}
              {employees.length === 0 && <p className="text-xs text-muted-foreground">Liste des Easers indisponible (Dolibarr) : la notification ira à toutes les Easers.</p>}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="notif-cancel">Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="notif-submit">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Bell className="h-4 w-4 mr-1" />} Envoyer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const adminNotificationImageUrl = (id, key) => `${BACKEND_URL}/api/admin/easer/notifications/${id}/image?key=${encodeURIComponent(key)}`;
